import { useState } from "react";
import { taahhutData, TaahhutKayit } from "@/mocks/takip";

const BIL_STYLE: Record<string, string> = {
  "Gönderildi": "bg-emerald-100 text-emerald-700",
  "Gönderilmedi": "bg-gray-100 text-gray-600",
  "Devam Ediyor": "bg-sky-100 text-sky-700",
  "Duraklatma Formu Gönderildi": "bg-violet-100 text-violet-700",
};

const FILTER_TABS = [
  { key: "tumu", label: "Tümü" },
  { key: "haftaBiter", label: "Bu Hafta Bitiyor" },
  { key: "15gun", label: "15 Gün İçinde" },
  { key: "30gun", label: "30 Gün İçinde" },
  { key: "duraklama", label: "Duraklatma Formu" },
  { key: "devam", label: "Devam Eden" },
];

interface ActionModalProps { kayit: TaahhutKayit; type: "bilgilendirme" | "duraklama" | "devam"; onClose: () => void; }
function ActionModal({ kayit, type, onClose }: ActionModalProps) {
  const [sent, setSent] = useState(false);
  const titles: Record<ActionModalProps["type"], string> = {
    bilgilendirme: "Bilgilendirme Mesajı Gönder",
    duraklama: "Duraklatma Formu Gönder",
    devam: "Devam Ediyor Olarak İşaretle",
  };
  const icons: Record<ActionModalProps["type"], string> = {
    bilgilendirme: "ri-message-3-line",
    duraklama: "ri-pause-circle-line",
    devam: "ri-checkbox-circle-line",
  };
  const colors: Record<ActionModalProps["type"], string> = {
    bilgilendirme: "bg-[#25D366]",
    duraklama: "bg-violet-600",
    devam: "bg-sky-500",
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !sent && onClose()} />
      <div className="relative bg-white rounded-2xl w-full max-w-sm shadow-2xl p-6 space-y-4">
        {sent ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-checkbox-circle-line text-emerald-600 text-2xl"></i>
            </div>
            <p className="text-sm font-semibold text-gray-800">İşlem tamamlandı!</p>
            <p className="text-xs text-gray-500 mt-1">{kayit.veliAdi} için kayıt güncellendi</p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className={`w-12 h-12 rounded-full ${colors[type]}/10 flex items-center justify-center mx-auto mb-3`}>
                <i className={`${icons[type]} text-2xl text-gray-700`}></i>
              </div>
              <h3 className="font-bold text-gray-900">{titles[type]}</h3>
              <p className="text-xs text-gray-500 mt-1">
                <strong>{kayit.veliAdi}</strong> – {kayit.ogrenciAdi} ({kayit.kalanGun} gün kaldı)
              </p>
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 text-sm border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 cursor-pointer">Vazgeç</button>
              <button
                onClick={() => { setSent(true); setTimeout(() => { setSent(false); onClose(); }, 1800); }}
                className={`flex-1 py-2.5 text-sm text-white rounded-xl cursor-pointer font-semibold ${colors[type]} hover:opacity-90`}
              >
                Onayla
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function TaahhutPage() {
  const [activeFilter, setActiveFilter] = useState("tumu");
  const [search, setSearch] = useState("");
  const [actionModal, setActionModal] = useState<{ kayit: TaahhutKayit; type: ActionModalProps["type"] } | null>(null);

  const in30 = taahhutData.filter(t => t.kalanGun <= 30 && t.kalanGun >= 0).length;
  const in15 = taahhutData.filter(t => t.kalanGun <= 15 && t.kalanGun >= 0).length;
  const inWeek = taahhutData.filter(t => t.kalanGun <= 7 && t.kalanGun >= 0).length;
  const duraklama = taahhutData.filter(t => t.bilgilendirmeDurumu === "Duraklatma Formu Gönderildi").length;
  const devam = taahhutData.filter(t => t.bilgilendirmeDurumu === "Devam Ediyor").length;

  const filtered = taahhutData.filter(t => {
    const s = search.toLowerCase();
    const matchSearch = !s || t.veliAdi.toLowerCase().includes(s) || t.ogrenciAdi.toLowerCase().includes(s);
    let matchFilter = true;
    if (activeFilter === "haftaBiter") matchFilter = t.kalanGun <= 7 && t.kalanGun >= 0;
    else if (activeFilter === "15gun") matchFilter = t.kalanGun <= 15 && t.kalanGun >= 0;
    else if (activeFilter === "30gun") matchFilter = t.kalanGun <= 30 && t.kalanGun >= 0;
    else if (activeFilter === "duraklama") matchFilter = t.bilgilendirmeDurumu === "Duraklatma Formu Gönderildi";
    else if (activeFilter === "devam") matchFilter = t.bilgilendirmeDurumu === "Devam Ediyor";
    return matchSearch && matchFilter;
  });

  const getKalanStyle = (gun: number) => {
    if (gun <= 7) return "text-red-600 bg-red-50";
    if (gun <= 15) return "text-orange-600 bg-orange-50";
    if (gun <= 30) return "text-amber-600 bg-amber-50";
    return "text-gray-700 bg-gray-100";
  };

  return (
    <div className="p-4 md:p-6 xl:p-8 space-y-6 max-w-[1600px]">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Taahhüt Takibi</h2>
          <p className="text-sm text-gray-500 mt-0.5">Taahhüt bitiş tarihlerini izleyin ve yenileme bilgilendirmesi gönderin</p>
        </div>
        <button className="px-5 py-2.5 text-sm bg-[#0B1120] text-white rounded-xl hover:bg-[#1a2a4a] cursor-pointer whitespace-nowrap flex items-center gap-2 font-semibold transition-colors">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-notification-3-line text-sm"></i></div>
          Toplu Bilgilendirme Gönder
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "30 Gün İçinde Bitiyor", value: in30, icon: "ri-calendar-close-line", color: "bg-amber-500 text-white" },
          { label: "15 Gün İçinde Bitiyor", value: in15, icon: "ri-alarm-warning-line", color: "bg-orange-500 text-white" },
          { label: "Bu Hafta Bitiyor", value: inWeek, icon: "ri-fire-line", color: "bg-red-500 text-white" },
          { label: "Duraklatma Formu", value: duraklama, icon: "ri-pause-circle-line", color: "bg-violet-500 text-white" },
          { label: "Devam Eden Taahhüt", value: devam, icon: "ri-checkbox-circle-line", color: "bg-[#25D366] text-white" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-[11px] text-gray-500 leading-tight">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row gap-3">
          <div className="flex flex-wrap gap-2 flex-1">
            {FILTER_TABS.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveFilter(t.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors
                  ${activeFilter === t.key ? "bg-[#0B1120] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-64">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
              <i className="ri-search-line text-sm"></i>
            </div>
            <input
              type="text"
              placeholder="İsim ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#25D366]/30 focus:border-[#25D366]"
            />
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {["Veli Adı", "Öğrenci Adı", "Taahhüt Başlangıcı", "Taahhüt Bitişi", "Kalan Gün", "Bilgilendirme Durumu", "İşlem"].map(h => (
                  <th key={h} className="text-left text-[11px] font-semibold text-gray-400 px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 text-xs font-bold flex-shrink-0">
                        {t.veliAdi.charAt(0)}
                      </div>
                      <span className="text-sm font-semibold text-gray-800 whitespace-nowrap">{t.veliAdi}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{t.ogrenciAdi}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{t.baslangic}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{t.bitis}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${getKalanStyle(t.kalanGun)}`}>
                      {t.kalanGun <= 0 ? "Bitti" : `${t.kalanGun} gün`}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold ${BIL_STYLE[t.bilgilendirmeDurumu]}`}>
                      {t.bilgilendirmeDurumu}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setActionModal({ kayit: t, type: "bilgilendirme" })}
                        title="Bilgilendirme Mesajı Gönder"
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#25D366]/10 text-[#25D366] hover:bg-[#25D366]/20 cursor-pointer transition-colors"
                      >
                        <i className="ri-whatsapp-line text-xs"></i>
                      </button>
                      <button
                        onClick={() => setActionModal({ kayit: t, type: "duraklama" })}
                        title="Duraklatma Formu Gönder"
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-50 text-violet-600 hover:bg-violet-100 cursor-pointer transition-colors"
                      >
                        <i className="ri-pause-line text-xs"></i>
                      </button>
                      <button
                        onClick={() => setActionModal({ kayit: t, type: "devam" })}
                        title="Devam Ediyor İşaretle"
                        className="w-7 h-7 flex items-center justify-center rounded-lg bg-sky-50 text-sky-600 hover:bg-sky-100 cursor-pointer transition-colors"
                      >
                        <i className="ri-check-line text-xs"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
                <i className="ri-calendar-close-line text-gray-400 text-xl"></i>
              </div>
              <p className="text-sm text-gray-400">Bu filtreye uygun kayıt bulunamadı</p>
            </div>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-gray-100">
          {filtered.map(t => (
            <div key={t.id} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-700 text-sm font-bold flex-shrink-0">
                    {t.veliAdi.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{t.veliAdi}</p>
                    <p className="text-xs text-gray-500">{t.ogrenciAdi}</p>
                  </div>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-lg ${getKalanStyle(t.kalanGun)}`}>
                  {t.kalanGun <= 0 ? "Bitti" : `${t.kalanGun} gün`}
                </span>
              </div>
              <div className="text-xs text-gray-500">Bitiş: <strong className="text-gray-800">{t.bitis}</strong></div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${BIL_STYLE[t.bilgilendirmeDurumu]}`}>
                {t.bilgilendirmeDurumu}
              </span>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => setActionModal({ kayit: t, type: "bilgilendirme" })}
                  className="flex-1 py-2 text-xs font-semibold bg-[#25D366]/10 text-[#25D366] rounded-xl cursor-pointer flex items-center justify-center gap-1"
                >
                  <i className="ri-whatsapp-line"></i> Bilgilendirme
                </button>
                <button
                  onClick={() => setActionModal({ kayit: t, type: "duraklama" })}
                  className="flex-1 py-2 text-xs font-semibold bg-violet-50 text-violet-600 rounded-xl cursor-pointer flex items-center justify-center gap-1"
                >
                  <i className="ri-pause-line"></i> Duraklatma
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-4 py-3 border-t border-gray-100">
          <p className="text-xs text-gray-400">{filtered.length} kayıt gösteriliyor</p>
        </div>
      </div>

      {actionModal && (
        <ActionModal
          kayit={actionModal.kayit}
          type={actionModal.type}
          onClose={() => setActionModal(null)}
        />
      )}
    </div>
  );
}